import { useState, useCallback } from 'react';
import { ComponentWithReplacement } from '../types';
import { Building } from '../../../../types/protocols';
import { BaseType } from '../../../../types/Enums';
import { getComponentRoomKey } from '../../../../Helpers/ComponentRoomUtils';
import { getFloorAreaVerificationState } from '../../../../Helpers/floorAreaVerification';

export interface AreaSettingsContext {
    componentKey: string;
    componentName: string;
    currentArea: number | null;
    /** A floor in a room whose golvyta is uppmätt på ritning: saving a new area makes that stale (web#1383). */
    inVerifiedRoom: boolean;
}

/** True for a floor whose room's floor-area verification is current. */
export const isFloorInVerifiedRoom = (component: ComponentWithReplacement['component'], building: Building | null): boolean => {
    if (!building || component.type !== BaseType.Floor) return false;
    const roomKey = getComponentRoomKey(component);
    const room = roomKey ? building.model.structure?.rooms?.[roomKey] : undefined;
    if (!room?.floorAreaVerification) return false;
    const floors = Object.values(building.model.structure?.components || {})
        .filter(c => c.type === BaseType.Floor && getComponentRoomKey(c) === roomKey);
    return getFloorAreaVerificationState(room.floorAreaVerification, floors).kind === 'verified';
};

interface UseAreaModalProps {
    building: Building | null;
    onSaveArea: (componentKey: string, newArea: number) => Promise<void>;
}

interface UseAreaModalReturn {
    showAreaModal: boolean;
    areaContext: AreaSettingsContext | null;
    handleOpenAreaModal: (plan: ComponentWithReplacement) => void;
    handleCloseAreaModal: () => void;
    handleSaveArea: (newArea: number) => Promise<void>;
}

/**
 * Hook for managing the area editing modal.
 * Delegates save operations to parent handler for consistency with other modal hooks.
 */
export const useAreaModal = ({
    building,
    onSaveArea
}: UseAreaModalProps): UseAreaModalReturn => {
    const [showAreaModal, setShowAreaModal] = useState<boolean>(false);
    const [areaContext, setAreaContext] = useState<AreaSettingsContext | null>(null);

    const handleOpenAreaModal = useCallback((plan: ComponentWithReplacement) => {
        const currentArea = building ? plan.component.totalArea(building) : null;

        setAreaContext({
            componentKey: plan.component.key,
            componentName: building ? plan.component.displayName(building) : 'Komponent',
            currentArea,
            inVerifiedRoom: isFloorInVerifiedRoom(plan.component, building),
        });
        setShowAreaModal(true);
    }, [building]);

    const handleCloseAreaModal = useCallback(() => {
        setShowAreaModal(false);
        setAreaContext(null);
    }, []);

    const handleSaveArea = useCallback(async (newArea: number): Promise<void> => {
        if (!areaContext) return;

        await onSaveArea(areaContext.componentKey, newArea);
        handleCloseAreaModal();
    }, [areaContext, onSaveArea, handleCloseAreaModal]);

    return {
        showAreaModal,
        areaContext,
        handleOpenAreaModal,
        handleCloseAreaModal,
        handleSaveArea
    };
};
